import { Badge, Button, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { useNavigate } from 'react-router';
import { useClientFamilyMap } from 'src/clients/hooks/useClientFamilyMap';
import { useCalendarEvents } from 'src/meetings/hooks/useCalendarEvents';
import {
  formatClientLabel,
  getClientDisplayNames,
  getFirstClientParticipant,
} from 'src/meetings/utils/event-utils';
import { formatShortDate, formatTime } from 'src/utils/dateFormatting';

const WINDOW_HOURS = 48;

function dayLabel(value: string): string {
  const date = new Date(value);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return formatShortDate(date);
}

export default function UpcomingMeetingsRail() {
  const navigate = useNavigate();
  const clientFamilyMap = useClientFamilyMap();
  const { events, isLoading } = useCalendarEvents({
    pastOrUpcoming: 'UPCOMING',
    clientMeetingsOnly: true,
  });

  const windowEnd = new Date();
  windowEnd.setHours(windowEnd.getHours() + WINDOW_HOURS);
  const imminent = events.filter(event => new Date(event.scheduledStartAt) <= windowEnd);

  if (!isLoading && imminent.length === 0) {
    return (
      <Text size="sm" c="gray.6">
        No client meetings in the next {WINDOW_HOURS} hours.
      </Text>
    );
  }

  return (
    <Stack gap="xs">
      {imminent.map(event => {
        const client = getFirstClientParticipant(event)?.participant.client ?? null;
        // The rail spans every client, so a meeting with two or more members of one
        // family reads as "<name> family" rather than whichever member comes first.
        const clientLabel = formatClientLabel(getClientDisplayNames(event, clientFamilyMap));
        const viewDestination = client
          ? `/client/${client.id}/meetings/upcoming/${event.id}`
          : `/meetings?eventId=${event.id}`;
        return (
          <UnstyledButton key={event.id} onClick={() => navigate(viewDestination)}>
            <Paper withBorder radius="md" p="sm">
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>
                    {event.title}
                  </Text>
                  <Text size="xs" c="gray.6">
                    {dayLabel(event.scheduledStartAt)}, {formatTime(event.scheduledStartAt)}
                    {clientLabel && <> &middot; {clientLabel}</>}
                  </Text>
                </Stack>
                {event.hasMeetingAgenda ? (
                  <Badge color="teal" variant="light" radius="sm">
                    Agenda ready
                  </Badge>
                ) : (
                  <Group gap="xs" wrap="nowrap">
                    <Badge color="orange" variant="light" radius="sm">
                      Needs agenda
                    </Badge>
                    {client && (
                      <Button
                        size="xs"
                        variant="default"
                        onClick={mouseEvent => {
                          mouseEvent.stopPropagation();
                          navigate(
                            `/client/${client.id}/meetings/upcoming/${event.id}?generate_agenda=true`,
                          );
                        }}
                      >
                        Generate
                      </Button>
                    )}
                  </Group>
                )}
              </Group>
            </Paper>
          </UnstyledButton>
        );
      })}
    </Stack>
  );
}
